import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import TemplatePreviewCard from "../TemplatePreviewCard";
import { ATTRIBUTE_GROUPS, type MappableLead, type VariableMapping } from "../variableMapping";

type StepVariableMappingProps = {
  templateVariables: string[];
  variableMapping: VariableMapping;
  onVariableMappingChange: (mapping: VariableMapping) => void;
  selectedTemplateBodyText: string | undefined;
  sampleLead: MappableLead | undefined;
};

const StepVariableMapping = ({
  templateVariables,
  variableMapping,
  onVariableMappingChange,
  selectedTemplateBodyText,
  sampleLead,
}: StepVariableMappingProps) => {
  return (
    <div className="flex flex-col gap-4">
      <div>
        <p className="mb-2 text-lg font-bold">Mapear Variables</p>
        <p className="mb-4 text-sm text-gray-600">
          Para cada variable en la plantilla, elige si usar un atributo del lead o texto estático.
        </p>
      </div>

      {templateVariables.length === 0 ? (
        <Card>
          <CardContent className="p-6">
            <p className="text-center text-gray-500">Esta plantilla no tiene variables.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="flex flex-col gap-4">
          {templateVariables.map((varName) => {
            const mapping = variableMapping[varName] || {
              source: "lead_attribute",
              value: "",
              fallback: "",
            };
            return (
              <Card key={varName}>
                <CardContent className="p-4">
                  <div className="mb-2 flex items-center gap-2">
                    <Badge variant="outline" className="font-mono">{`{{${varName}}}`}</Badge>
                  </div>
                  <div className="grid gap-3 md:grid-cols-3">
                    <div>
                      <Label className="text-xs">Fuente</Label>
                      <Select
                        value={mapping.source}
                        onValueChange={(v) =>
                          onVariableMappingChange({
                            ...variableMapping,
                            [varName]: {
                              ...mapping,
                              source: v as "lead_attribute" | "static",
                              value: "",
                            },
                          })
                        }
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="lead_attribute">Atributo dinámico</SelectItem>
                          <SelectItem value="static">Texto estático</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label className="text-xs">
                        {mapping.source === "lead_attribute" ? "Atributo" : "Valor"}
                      </Label>
                      {mapping.source === "lead_attribute" ? (
                        <Select
                          value={mapping.value}
                          onValueChange={(v) =>
                            onVariableMappingChange({
                              ...variableMapping,
                              [varName]: { ...mapping, value: v },
                            })
                          }
                        >
                          <SelectTrigger className="mt-1">
                            <SelectValue placeholder="Seleccionar" />
                          </SelectTrigger>
                          <SelectContent>
                            {ATTRIBUTE_GROUPS.map((group) => (
                              <SelectGroup key={group.label}>
                                <SelectLabel>{group.label}</SelectLabel>
                                {group.options.map((attr) => (
                                  <SelectItem key={attr.value} value={attr.value}>
                                    {attr.label}
                                  </SelectItem>
                                ))}
                              </SelectGroup>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <Input
                          value={mapping.value}
                          onChange={(e) =>
                            onVariableMappingChange({
                              ...variableMapping,
                              [varName]: { ...mapping, value: e.target.value },
                            })
                          }
                          placeholder="Texto fijo"
                          className="mt-1"
                        />
                      )}
                    </div>

                    {mapping.source === "lead_attribute" && (
                      <div>
                        <Label className="text-xs">Valor por defecto</Label>
                        <Input
                          value={mapping.fallback || ""}
                          onChange={(e) =>
                            onVariableMappingChange({
                              ...variableMapping,
                              [varName]: { ...mapping, fallback: e.target.value },
                            })
                          }
                          placeholder="Si el atributo está vacío"
                          className="mt-1"
                        />
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}

          <TemplatePreviewCard
            bodyText={selectedTemplateBodyText}
            variableMapping={variableMapping}
            sampleLead={sampleLead}
          />
        </div>
      )}
    </div>
  );
};

export default StepVariableMapping;
